import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { composerStyles as styles } from './composerStyles';
import type { ComposerReplyType } from './types';
import { parseEmails } from './useComposerSend';

interface ComposerAddressFieldsProps {
	replyType: ComposerReplyType;
	ticket: ISupportTicket;
	replyToValue: string;
	setReplyToValue: React.Dispatch<React.SetStateAction<string>>;
	forwardToValue: string;
	setForwardToValue: React.Dispatch<React.SetStateAction<string>>;
	ccValue: string;
	setCcValue: React.Dispatch<React.SetStateAction<string>>;
	bccValue: string;
	setBccValue: React.Dispatch<React.SetStateAction<string>>;
	showCc: boolean;
	setShowCc: React.Dispatch<React.SetStateAction<boolean>>;
	showBcc: boolean;
	setShowBcc: React.Dispatch<React.SetStateAction<boolean>>;
}

/** To / Cc / Bcc hairline rows for the composer. Notes never send email, so nothing renders there. */
export const ComposerAddressFields: React.FC<ComposerAddressFieldsProps> = ({
	replyType,
	ticket,
	replyToValue,
	setReplyToValue,
	forwardToValue,
	setForwardToValue,
	ccValue,
	setCcValue,
	bccValue,
	setBccValue,
	showCc,
	setShowCc,
	showBcc,
	setShowBcc,
}) => {
	if (replyType === 'note') return null;

	const isForward = replyType === 'forward';
	const csm = ticket.csm?.email?.trim();
	const ccEmails = parseEmails(ccValue).map(value => value.toLowerCase());
	const showAddCsm = !isForward && !!csm && !ccEmails.includes(csm.toLowerCase());
	const csmLabel = ticket.csm?.firstName ? `+ cc ${ticket.csm.firstName} (CSM)` : '+ cc CSM';

	return (
		<div className={css(styles.addressFields)}>
			<div className={css(styles.addressRow)}>
				<span className={css(styles.addressLabel)}>To</span>
				{isForward ? (
					<Input
						styles={[styles.addressInput]}
						type='text'
						placeholder='Enter recipient emails, comma-separated'
						value={forwardToValue}
						onChange={event => setForwardToValue(event.target.value)}
						autoFocus
					/>
				) : (
					<Input
						styles={[styles.addressInput]}
						type='text'
						placeholder='Recipient email'
						aria-label='Reply recipient'
						value={replyToValue}
						onChange={e => setReplyToValue(e.target.value)}
					/>
				)}
				{!showCc && (
					<Button variant='ghost' size='sm' styles={[styles.addressToggleBtn]} onClick={() => setShowCc(true)}>
						cc
					</Button>
				)}
				{!showBcc && (
					<Button variant='ghost' size='sm' styles={[styles.addressToggleBtn]} onClick={() => setShowBcc(true)}>
						bcc
					</Button>
				)}
			</div>
			{showCc && (
				<div className={css(styles.addressSubRow)}>
					<span className={css(styles.addressLabel)}>Cc</span>
					<Input
						styles={[styles.addressInput]}
						type='text'
						placeholder='name@company.com'
						value={ccValue}
						onChange={event => setCcValue(event.target.value)}
					/>
					{showAddCsm && csm && (
						<Button
							variant='ghost'
							size='sm'
							styles={[styles.addressToggleBtn]}
							onClick={() => setCcValue(prev => (prev.trim() ? `${prev}, ${csm}` : csm))}
						>
							{csmLabel}
						</Button>
					)}
				</div>
			)}
			{showBcc && (
				<div className={css(styles.addressSubRow)}>
					<span className={css(styles.addressLabel)}>Bcc</span>
					<Input
						styles={[styles.addressInput]}
						type='text'
						placeholder='name@company.com'
						value={bccValue}
						onChange={event => setBccValue(event.target.value)}
					/>
				</div>
			)}
		</div>
	);
};
